import React, { useEffect } from 'react';
import ProductControls from './ProductControls';
import OptimizedPicture from './OptimizedPicture';
import { getProductImageSources } from '../utils/productImageUtils';
import { formatPricePerUnit, isBulkCatalogProduct } from '../../../utils/bulkSaleUtils';

const formatPrice = (price) => {
  if (price == null || isNaN(price)) return '';
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(price);
};

const ProductDetailModal = ({
  product,
  branchConfig,
  count,
  onAdd,
  onMinus,
  onClose,
}) => {
  useEffect(() => {
    if (!product) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [product, onClose]);

  if (!product) return null;

  const displaySources = getProductImageSources(product, 'large');

  const showPrice = branchConfig?.priceActive;
  const showControls = branchConfig?.status;
  const hasBrand = product.brandName != null && product.brandName !== '';
  const isBulk = isBulkCatalogProduct(product);

  const renderImage = () => {
    if (!displaySources.hasImage) return null;

    if (displaySources.hasBase64Image) {
      return (
        <img
          src={displaySources.imageUrl}
          alt={product.name}
          className="carta-v7-product-detail__image"
          loading="eager"
          decoding="async"
        />
      );
    }

    if (displaySources.hasOptimizedImage && displaySources.optimizedImageUrls) {
      return (
        <OptimizedPicture
          srcJpg={displaySources.optimizedImageUrls.jpg || displaySources.imageUrl}
          srcWebp={displaySources.optimizedImageUrls.webp}
          srcAvif={displaySources.optimizedImageUrls.avif}
          alt={product.name}
          className="carta-v7-product-detail__image"
          loading="eager"
          decoding="async"
        />
      );
    }

    return (
      <img
        src={displaySources.imageUrl}
        alt={product.name}
        className="carta-v7-product-detail__image"
        loading="eager"
        decoding="async"
      />
    );
  };

  return (
    <div
      className="carta-v7-product-detail"
      role="dialog"
      aria-modal="true"
      aria-labelledby="carta-v7-product-detail-title"
      onClick={onClose}
    >
      <div
        className="carta-v7-product-detail__sheet"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="carta-v7-product-detail__close"
          onClick={onClose}
          aria-label="Cerrar detalle del producto"
        >
          ×
        </button>

        {displaySources.hasImage && (
          <div className="carta-v7-product-detail__image-wrap">
            {renderImage()}
          </div>
        )}

        <div className="carta-v7-product-detail__body">
          {hasBrand && (
            <span className="carta-v7-product-detail__brand">{product.brandName}</span>
          )}
          <h2 className="carta-v7-product-detail__title" id="carta-v7-product-detail-title">
            {product.name}
          </h2>
          {product.description && (
            <p className="carta-v7-product-detail__desc">{product.description}</p>
          )}
          {isBulk && (
            <p className="carta-v7-product-detail__bulk-hint">
              Venta a granel: indicá cuánto querés o el monto a gastar.
            </p>
          )}
          {showPrice && (
            <p className="carta-v7-product-detail__price">
              {isBulk
                ? formatPricePerUnit(product.price, product.measurementUnit)
                : formatPrice(product.price)}
            </p>
          )}
          {showPrice && showControls && (
            <div className="carta-v7-product-detail__controls">
              <ProductControls
                productId={product.id}
                count={count}
                onAdd={onAdd}
                onMinus={onMinus}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailModal;
